import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ShoppingBag, Package, Heart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/site/SiteBits";
import { usePageMeta, useLive } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { shopApi, gbp2 } from "@/lib/shopApi";
import { CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";
import { ShopRails, SavedProducts } from "@/components/shop/ShopExtras";
import { AgentBrief } from "@/components/agents/Agents";

const PRICE_RANGES = { u10: { max_price: 10 }, u20: { max_price: 20 }, "20-50": { min_price: 20, max_price: 50 }, "50p": { min_price: 50 } };

export function ShopSaved() {
  return <SavedProducts Card={ProductCard} />;
}

export function ProductCard({ p }) {
  const off = p.compare_at_gbp && p.compare_at_gbp > p.price_gbp;
  return (
    <Link to={`/shop/${p.id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card card-hover" data-testid={`product-${p.id}`}>
      <div className="relative aspect-square overflow-hidden bg-muted">
        {p.cover && <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        {p.sold_out && <span className="absolute left-3 top-3 rounded-full bg-foreground px-2.5 py-1 text-[11px] font-semibold text-background">Sold out</span>}
        {off && !p.sold_out && <span className="absolute left-3 top-3 rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white">Save {gbp2(p.compare_at_gbp - p.price_gbp)}</span>}
      </div>
      <div className="p-3.5">
        <h3 className="truncate font-semibold text-[15px] leading-tight">{p.title}</h3>
        <p className="mt-1 font-mono-num text-sm font-bold">{gbp2(p.price_gbp)} {off && <span className="ml-1 font-normal text-muted-foreground line-through">{gbp2(p.compare_at_gbp)}</span>}</p>
        {p.seller && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <CreatorAvatar author={p.seller} size={16} /> <span className="truncate">{p.seller.business_name || p.seller.display_name}</span> <VerifiedMark author={p.seller} size={12} />
            {p.rating?.count ? <span className="ml-auto inline-flex shrink-0 items-center gap-0.5"><Star size={11} className="fill-amber-400 text-amber-400" />{p.rating.avg}</span> : p.stats?.sold > 0 && <span className="ml-auto shrink-0">{p.stats.sold} sold</span>}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function Shop() {
  usePageMeta("Shop", "Merch, prints, beauty, food and digital goods from the creators and partners you follow and book on Fable +.");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "new";
  const q = params.get("q") || "";
  const price = params.get("price") || "";
  const [search, setSearch] = useState(q);
  const cats = useLive(() => shopApi.categories(), []);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [state, setState] = useState("loading");
  const seq = useRef(0);

  const load = useCallback(async (cur, replace) => {
    const my = ++seq.current;
    setState(replace ? "loading" : "more");
    try {
      const d = await shopApi.feed({ category: category !== "all" ? category : undefined, q: q || undefined, sort, cursor: cur || undefined, limit: 24, ...(PRICE_RANGES[price] || {}) });
      if (my !== seq.current) return;
      setItems((prev) => {
        const base = replace ? [] : prev;
        const seen = new Set(base.map((x) => x.id));
        return [...base, ...(d.items || []).filter((x) => !seen.has(x.id))];
      });
      setCursor(d.next_cursor || null);
      setState("idle");
    } catch {
      if (my === seq.current) setState("error");
    }
  }, [category, q, sort, price]);

  useEffect(() => { load(null, true); }, [load, user?.id]);

  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v && v !== "all" ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };

  return (
    <div>
      <PageHeader title="Shop the people you follow." lede="Merch, prints, beauty, food and digital goods — sold by the creators and partners on Fable +, shipped by them, paid through Stripe.">
        <form onSubmit={(e) => { e.preventDefault(); set({ q: search.trim() }); }} className="flex max-w-lg items-center gap-2" role="search">
          <label className="sr-only" htmlFor="shop-q">Search products</label>
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input id="shop-q" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products" data-testid="shop-search" className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <Button type="submit" className="rounded-full px-5">Search</Button>
        </form>
      </PageHeader>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="shop-categories">
            {(cats?.items || [{ key: "all", label: "For you" }]).map((c) => (
              <button key={c.key} type="button" onClick={() => set({ category: c.key })} aria-pressed={category === c.key} data-testid={`shop-cat-${c.key}`} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium ${category === c.key ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>{c.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <label className="sr-only" htmlFor="shop-price">Price</label><select id="shop-price" value={price} onChange={(e) => set({ price: e.target.value })} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm" data-testid="shop-price"><option value="">Any price</option><option value="u10">Under £10</option><option value="u20">Under £20</option><option value="20-50">£20–£50</option><option value="50p">£50+</option></select>
            <label className="sr-only" htmlFor="shop-sort">Sort</label>
            <select id="shop-sort" value={sort} onChange={(e) => set({ sort: e.target.value })} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm" data-testid="shop-sort">
              <option value="new">Newest</option><option value="popular">Best sellers</option><option value="rated">Top rated</option><option value="deals">Deals</option><option value="price_asc">Price: low to high</option><option value="price_desc">Price: high to low</option>
            </select>
            {user && <Link to="/shop/saved" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline" data-testid="shop-saved-link"><Heart size={14} /> Saved</Link>}
            {user && <Link to="/shop/orders" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"><Package size={14} /> My orders</Link>}
          </div>
        </div>

        {category === "all" && !q && <div className="mt-6"><AgentBrief agent="zoe" /></div>}
        {category === "all" && !q && sort === "new" && !price && <ShopRails />}
        {state === "loading" ? (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}</div>
        ) : state === "error" ? (
          <p className="mt-10 text-center text-muted-foreground">Couldn't load the shop right now. Please try again shortly.</p>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border p-10 text-center">
            <ShoppingBag size={24} className="mx-auto text-muted-foreground" />
            <p className="mt-2 font-semibold">Nothing here yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Try another category or search.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="shop-grid">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
            {cursor && (
              <div className="mt-6 text-center"><Button variant="outline" className="rounded-full px-8" disabled={state === "more"} onClick={() => load(cursor, false)}>{state === "more" ? "Loading…" : "Load more"}</Button></div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
